import { IonImg, IonModal } from '@ionic/react';
import React, { ComponentProps, useLayoutEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faPhone, faGlobe, faDiamondTurnRight } from '@fortawesome/free-solid-svg-icons';
type IonModalProps = ComponentProps<typeof IonModal>;

type Props = {
  onDismiss: () => void;
};

type InsightPopupProps = {
  isOpen: boolean;
  activeInsight: string;
  setShowIonModal: (isOpen: boolean) => void;
};

export const TrendsSectionInfo = ({ onDismiss }: Props) => (
  <div className="dark-popup">
    <div className="dark-popup-content">
      <h2>Hot Slot Insight Trends</h2>
      <p>
        Knowing how various insights are trending helps you get a feel for how this slot has been
        paying out and how you think it may payout in the future.
      </p>
      <p>
        The PRO version of SlotCheck also includes the most recent 1,4 and 8 hours when in landscape
        mode.
      </p>
      <p>
        Slots results are random, but seeing trends give your gut and intuition a better chance to
        maximize your gaming enjoyment.
      </p>
    </div>
    <div className="dark-popup-footer" onClick={onDismiss}>
      Close
    </div>
  </div>
);

export const Pop = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Payout to Player (POP)</h2>
        <p>
        POP tells the player what percentage of the money played (Play, bet, paid-in, or wagered) will be returned to the player over the game’s lifetime. Each slot has a set POP as determined by the manufacturer, casino, and regulatory authority. The minimum POP that a slot machine can be set at is enforced by the regulatory agency in each jurisdiction. Check the web site of your state, tribe, or provincial gaming agency. Typically, the minimum POP is 75% (e.g., Nevada) to 80% (e.g., Wisconsin) in most jurisdictions. As you can see from the Slot Check app, POP for most games are set significantly more generous than the jurisdictional minimum. Video poker slots typically have the highest POP at around 96% to 98%.
        </p>
        <p>
        Remember, POP is what the game can be expected to pay out over its lifetime as defined by lifetime Win divided by lifetime Play (LT Win/LT Play = POP). To see how any given game in Slot Check has been paying out recently, you must look at RTP (Return to Player).
        </p>
        <p>
        Players think about POP. Casinos think about Hold (100% - POP = Hold).
        </p>
        <p>
          Each game has a unique personality. Some games play better, or are more fun to play, when set at a lower POP. For example, a POP set at 93% does not mean that it is a better or a more fun game than one set at 90%. It also does not mean that in the short term (1, 2, or 3 visits) you will have a greater chance of a positive RTP playing the higher POP game. Considering other factors such as your budget, what type, style of games you like to play, and looking at PRO insights such as how RTP has been trending may improve your play and give you more entertainment value. The good news is that Slot Check is the first only app in the world that gives you all the information you need to make the most informed decisions in gaming!
        </p>
      </div>
      <div
        className="dark-popup-footer"
        onClick={() => {
          onDismiss();
        }}
      >
        Close
      </div>
    </div>
  );
};

export const Return = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Return to Player (RTP) </h2>
        <p>
        Mathematically, RTP is the same calculation as POP. The difference is that RTP tells the player how the slot has actually paid out recently (e.g., 1, 4, 8, or 24 hours, or last 7 or 30 days). The calculation is Money Won divided by Money Bet ($ Won/$ Bet) for any given timeframe selected = RTP. RTP tells the player what percentage of the money Paid-in (played, bet, or wagered) was returned to the player in the selected timeframe.
        </p>
        <p>
          When looking at POP and RTP together, the player can see if the slot has been over or under performing for any selected timeframe relative to POP. Comparing RTP to breakeven will also tell a similar story.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const Spins = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Spins (S)</h2>
        <p>
        This insight tells the total number of times a slot machine has been spun.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const Vol = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Volatility (VOL) </h2>
        <p>
        Slots are generally considered to have high or low volatility. Volatility helps the player better understand the personality of the game.
        </p>
        <p>
        High Volatility slots are those that typically do not win often but when they do they pay big. They are sometimes referred to as “high and fast” as your money can go quick but if you get lucky the Money Won can be big.
        </p>
<p>
Low Volatility slots are those that typically win often but the wins are modest vs the big wins of high volatility slots. They are sometimes referred to as “low and slow” as they typically allow the player's budget to last longer giving them more time on de.
</p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const Jackpot = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Jackpots (JKPT)</h2>
        <p>
        This insight is the total amount of money won from hand pay jackpots, $1200 or more. Note that with the passing of the One Big Beautiful Bill Act (OBBBA) the reporting threshold for slot machine winnings will be increased to $2,000 starting January 1, 2026.
        </p>
        <p>
          Jackpots in Slot Check are expressed by both dollar amount won and number of jackpots won. This insight includes jackpots won using free play.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const FreePlayKjackpots = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Free Play Jackpots (FPJ)  </h2>
        <p>
        This insight tells you which jackpots have been won with free play.  Free Play Jackpots are expressed by both dollar amount won, and number of jackpots won and are abbreviated as FPJ. 
        </p>
      </div>
      <div
        className="dark-popup-footer"
        onClick={() => {
          onDismiss();
        }}
      >
        Close
      </div>
    </div>
  );
};

export const MoneyWon = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Total Win (Win)</h2>
        <p>
        This insight is the total amount of money won or paid back (Win) regardless of how much has been played (bet or wagered). This insight includes jackpots. In gaming industry speak, Win = (Coin-out + Jackpots).
        </p>
      </div>
      <div
        className="dark-popup-footer"
        onClick={() => {
          onDismiss();
        }}
      >
        Close
      </div>
    </div>
  );
};

export const Played = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Total Played (Played)</h2>
        <p>
        This insight is the total amount of money paid-in, played, bet, or wagered (Play) regardless of the payout or Total Win.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const NoOfJackpot = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2># Jackpots (# JKPT)</h2>
        <p>
        The insight is the total number of hand pay jackpots.  Handpay jackpots are $1200 or more.  Jackpots are expressed by both dollar amount won, and number of jackpots won.  This insight includes jackpots won using free play. 
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};





export const WinSpin = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2> Win/Spin (W/S)  </h2>
        <p>
        This insight tells you how much a given slot has paid out per spin over the selected period. W/S is calculated by dividing Total Win (Win or payout) by the number of spins for a selected period. This number is expressed as a dollar amount ($) and is abbreviated W/S.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const NonJackpotWinSpin = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2> Non-Jackpot Win per Spin (W/S-J) </h2>
        <p>
        This insight is calculated by subtracting the total number of jackpots from Win and dividing by total spins ((Win – Jackpots)/Spins). This insight is expressed as a dollar amount and is abbreviated as W/S-J. When compared to W/S, you can see just how much of the W/S is coming from non-jackpot wins.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const JackpotWinSpin = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2> Jackpot per Spin (J/S) </h2>
        <p>
        This insight tells the player what percentage (%) or Win per Spin are from jackpot winnings. This insight is calculated by dividing J/S by W/S. This insight is expressed as a dollar amount and is abbreviated as J%W/S.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const JackpotWinSpinPercentage = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2> Jackpots as a % of Win/Spin (%J/S) </h2>
        <p>
        This insight tells the player what percentage (%) or wins per spin are from jackpot winnings.  This insight is calculated by dividing J/S by W/S.  This insight is expressed as % and is abbreviated as %J/S. 
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const WinRate = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <h2>Winning Spins (WS) </h2>
        <p>
          This insight tells you how often a spin will result in a winning spin for a selected
          timeframe. This insight is expressed as a percentage (%).
        </p>
        <h2>How do I think about WS?</h2>
        <p>By itself, you can find the slots on the floor that have had the most winning spins.</p>
        <p>
          While it can be assumed that all players love winning spins, WS by itself does not tell
          you how big those winning spins are or how profitable a slot is. For this, you must look
          to Volatility (VOL) and Return to player (RTP).
        </p>
        <p>
          Low Volatility slots are typically those that have a high percentage of winning spins but
          the winning spins are typically not high dollar and thus have lower RTP. These are slots
          that typically provide more time on device (longer slot play) and the frequency of winning
          spins make them fun to play.
        </p>
        <p>
          By contrast, high volatility games have low percent of winning spins but high RTP. These
          games are riskier and your money can go quickly, but when they do hit a winning spin it
          can be big.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const ColdestRTP = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <p>
          {' '}
          <span className="bold-text-popup">Return to Player (RTP)</span>
        </p>
        <p>
        Mathematically, RTP is the same calculation as POP.  The difference is that RTP tells the player how the slot has actually paid out recently (e.g., 1, 4, 8, or 24 hours, 7 days, 30 days, or since last jackpot). When looking at POP and RTP together, the player can see if the slot has been over or under performing for any selected timeframe relative to POP. 
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};
export const HottestRTP = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <p>
          {' '}
          <span className="bold-text-popup">Return to Player (RTP)</span>
        </p>
        <p>
        Mathematically, RTP is the same calculation as POP.  The difference is that RTP tells the player how the slot has actually paid out recently (e.g., 1, 4, 8, or 24 hours, 7 days, 30 days, or since last jackpot). When looking at POP and RTP together, the player can see if the slot has been over or under performing for any selected timeframe relative to POP. 
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};
export const ComparePlanSLJ = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <p>SLJ information goes to here.</p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const FindInsights = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup">
      <div className="dark-popup-content">
        <p>
          Everyone defines &quot;Hot Slots&quot; differently. Below are ten categories of slot
          insights that slot players have told us are valuable to consider when determining what
          slots to play. The information presented is historical (in the recent past) and does not
          predict future play as slot machines generate spin results randomly.
        </p>
      </div>
      <div className="dark-popup-footer" onClick={onDismiss}>
        Close
      </div>
    </div>
  );
};

export const LOTC = ({ onDismiss }: Props) => {
  return (
    <div className="dark-popup lotc-popup-container">
      <div className="dark-popup-content">
        <div className="lotc-popup-logo">
          <IonImg src="assets/images/LOT_property_logo.png" />
        </div>
        <div className="lotc-scroll-content">
          <div className="lotc-popup-heading">Lake of the Torches Resort Casino</div>
          <div className="lotc-popup-content">
            Please go to the Lake Club and signup for a player card and get your player ID. Linking
            your player ID in Slot Check will allow you to track your play, see your jackpots, and
            see yourself on the leaderboard
          </div>
          <div className="lotc-popup-content">
            510 Old Abe Rd, <br />
            Lac Du Flambeau, <br />
            WI 54538, <br />
            United States
          </div>
          <div className="lotc-popup-connect">
            <div
              className="lotc-popup-connect-box"
              onClick={() => {
                window.open('tel:18002586724');
              }}
            >
              <div className="lotc-popup-connect-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div className="lotc-popup-connect-text">Call</div>
            </div>

            <div
              className="lotc-popup-connect-box"
              onClick={() => {
                window.open('https://lakeofthetorches.com', '_blank');
              }}
            >
              <div className="lotc-popup-connect-icon">
                <FontAwesomeIcon icon={faGlobe} />
              </div>
              <div className="lotc-popup-connect-text">Website</div>
            </div>

            <div
              className="lotc-popup-connect-box"
              onClick={() => {
                window.open(
                  'https://www.google.com/maps/dir//Lake+of+the+Torches+Resort+Casino+510+Old+Abe+Rd+Lac+Du+Flambeau,+WI+54538+United+States/@45.9741614,-89.8917995,16z/data=!4m5!4m4!1m0!1m2!1m1!1s0x4d55f3d0e03d2df1:0x812ed45bb04baf75',
                  '_blank'
                );
              }}
            >
              <div className="lotc-popup-connect-icon">
                <FontAwesomeIcon icon={faDiamondTurnRight} />
              </div>
              <div className="lotc-popup-connect-text">Direction</div>
            </div>
          </div>
          <div className="dark-popup-footer" onClick={onDismiss}>
            <FontAwesomeIcon icon={faClose} />
          </div>
        </div>
      </div>
    </div>
  );
};

const InsightModal: React.FC<IonModalProps> = (props) => {
  const modalRef = useRef<HTMLIonModalElement>(null);

  useLayoutEffect(
    () => () => {
      // hack to fix
      modalRef.current?.dismiss();
    },
    []
  );

  return (
    <div>
      <IonModal ref={modalRef} {...props} isOpen={true} className="insight-modal">
        {props.children}
      </IonModal>
    </div>
  );
};

export const InsightPopUp = ({ isOpen, activeInsight, setShowIonModal }: InsightPopupProps) => {
  return (
    <>
      {isOpen && (
        <InsightModal isOpen={true} onDidDismiss={() => setShowIonModal(false)}>
          {activeInsight === 'money_won' && (
            <MoneyWon
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'return' && (
            <Return
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'spins' && (
            <Spins
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'pop' && (
            <Pop
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'volatility_rating' && (
            <Vol
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'jackpot_value' && (
            <Jackpot
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'jackpots' && (
            <NoOfJackpot
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'money_played' && (
            <Played
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'avg_money_won_per_spin' && (
            <WinSpin
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'win_rate' && (
            <WinRate
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'coldest_rtp' && (
            <ColdestRTP
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'hottest_rtp' && (
            <HottestRTP
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'trends_section_info' && (
            <TrendsSectionInfo
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'compare-plan-slj' && (
            <ComparePlanSLJ
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'find-insights' && (
            <FindInsights
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
          {activeInsight === 'lotc' && (
            <LOTC
              onDismiss={() => {
                setShowIonModal(false);
              }}
            />
          )}
        </InsightModal>
      )}
    </>
  );
};

export default InsightModal;
